<script setup lang="ts">
import { useFormContainer } from "~/composables/useFormContainer";
import FormCard from "~/components/ui/FormCard.vue";
import BackButton from "~/components/inputs/BackButton.vue";
import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
import Select from "~/components/inputs/SelectNew.vue";
import RadioButton from "~/components/inputs/RadioButton.vue";

import Validator from "~/utils/validator";

import type { IValue } from "~/interfaces";

const locale = await getLocale("forms", "employer_data");
const localeLoan = await getLocale("forms", "employee_loan");

import { useVuelidate } from "@vuelidate/core";
import employerFormRules from "~/rules/employerDataShort";
import { upperCaseMask } from "~/utils/masks";
import Accordion from "../ui/Accordion.vue";

defineComponent({
  name: "EmployerDataShort",
});

const props = defineProps({
  progress: { type: Number, required: true },
  title: { type: String, required: true },
  showEmployeeLoan: { type: Boolean, default: false },
  showFamilyMemberCount: { type: Boolean, default: true },
  allowEmployedFamilyMemberCount: { type: Boolean, default: true },
  stickyButton: { type: Boolean, default: false }
});

const { FormContainer } = useFormContainer();

const emit = defineEmits(["init", "submit", "goBack"]);

const employerName = useState<string | null>("employerName", () => null);
const employeeLoan = useState<boolean | null>("employeeLoan", () => null);
const familyMemberCount = useState<number | null>(
  "familyMemberCount",
  () => null,
);
const employedFamilyMemberCount = useState<number | null>(
  "employedFamilyMemberCount",
  () => null,
);

onMounted(async () => {
  emit("init");
});

const familyMembersCountValues = computed(() => {
  const arr: IValue[] = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => ({
    value: i,
    label: i.toString(),
  }));
  arr.unshift({
    value: null,
    label: locale.family_members_count,
    disabled: true,
  });
  return arr;
});

const employedFamilyMemberCountValues = computed(() => {
  const arr: IValue[] = [
    {
      value: null,
      label: locale.employed_family_members_count,
      disabled: true,
    },
  ];
  if (familyMemberCount.value) {
    for (let i = 1; i <= familyMemberCount.value; i++) {
      arr.push({ value: i, label: i.toString() });
    }
  }
  return arr;
});

const showEmployedFamilyMemberCount = computed(() => {
  if (familyMemberCount.value && props.showFamilyMemberCount && props.allowEmployedFamilyMemberCount) {
    return familyMemberCount.value > 0;
  }
  return false;
});
watch(familyMemberCount, async (newValue) => {
  if (newValue === 1) {
    employedFamilyMemberCount.value = 1;
    return;
  }
  if (
    newValue &&
    employedFamilyMemberCount.value &&
    newValue < employedFamilyMemberCount.value
  ) {
    employedFamilyMemberCount.value = newValue;
  }
  if (v$.value.employedFamilyMemberCount.$error) {
    v$.value.employedFamilyMemberCount.$reset();
  }
});

const v$ = useVuelidate(employerFormRules, {
  employerName,
  employeeLoan,
  familyMemberCount,
  employedFamilyMemberCount,
  showEmployeeLoan: props.showEmployeeLoan,
  showFamilyMemberCount: props.showFamilyMemberCount,
});

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = () => {
  if (validate()) {
    emit("submit");
  }
};
</script>

<template>
  <FormContainer :progress="progress">
      <FormCard :title="locale.title" @submit="submit" :has-sticky-button="stickyButton">
        <p class="pageSubtitle" v-html="locale.subtitle" />

        <div class="w-full">
          <div class="w-full mb-2">
            <div class="lg:pr-4 lg:w-1/3">
              <InputFloatingLabel
              v-model="employerName"
              :label="locale.denomination"
              type="text"
              :mask="upperCaseMask"
              :error="v$.employerName.$error"
              :error-message="Validator.message(v$.employerName)"
              test-id="employerName"
              />
            </div>
            <span class="text-sm italic">
              {{ locale.denomination_text }}
            </span>
          </div>
        </div>

        <div v-if="showEmployeeLoan" class="w-full mb-4 mt-8">
          <h1 class="pageTitle" v-html="localeLoan.title"/>
          <div class="flex mt-4 justify-center sm:justify-start">
            <RadioButton
              v-model="employeeLoan"
              :values="yesNoOptions"
              :error="v$.employeeLoan.$error"
              :error-message="Validator.message(v$.employeeLoan)"
              data-testid="employeeLoan"
            />
          </div>
        
          <Accordion>
            <p class="w-full text-sm text-center md:text-left " v-html="localeLoan.cdq_definition" />
            <template #items>
              <div class="yellowBox mt-5" v-html="localeLoan.cdq_explanation" />
            </template>
          </Accordion>
        </div>

        <h1 class="pageTitle mb-4 mt-8" v-if="showFamilyMemberCount">{{ locale.family_members_title  }}</h1>

        <div
          v-if="showFamilyMemberCount"
          class="w-full md:w-full lg:w-1/2 xl:w-1/3 lg:pr-4 mb-2"
        >
          <Select
            v-model="familyMemberCount"
            :values="familyMembersCountValues"
            :label="locale.family_members_count"
            floating
            disable-zero-value
            :error="v$.familyMemberCount.$error"
            :error-message="Validator.message(v$.familyMemberCount)"
            test-id="familyMemberCount"
          />
        </div>
        <div
          v-if="showEmployedFamilyMemberCount"
          class="w-full md:w-full lg:w-1/2 xl:w-1/3 xl:pr-4 mb-2"
        >
          <Select
            v-model="employedFamilyMemberCount"
            :values="employedFamilyMemberCountValues"
            :label="locale.employed_family_members_count"
            floating
            disable-zero-value
            :error="v$.employedFamilyMemberCount.$error"
            :error-message="Validator.message(v$.employedFamilyMemberCount)"
            test-id="employedFamilyMemberCount"
          />
        </div>
        <template #link>
          <BackButton :event="`goBack`" @go-back="$emit('goBack')" />
        </template>
      </FormCard>
  </FormContainer>
</template>

<style scoped></style>
